import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, Button, FormControl } from '@material-ui/core';
import { ZoomIn, ZoomOut, ZoomOutMap, History } from '@material-ui/icons';
import { Select } from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  BookAppearanceModifiersContext,
  BookAppearanceValuesContext,
} from 'contexts';
import IconButton from 'components/IconButton';
import { AriaLiveRegion, IconTooltip, SelectMenu } from 'components';
import {
  FONT_SIZE,
  MAX_FONT_SIZE_VALUE,
  MIN_FONT_SIZE_VALUE,
  FONT_FAMILY,
  THEME,
  THEME_DEFAULT,
  THEME_HIGH_CONTRAST,
  LETTER_SPACING,
  LINE_HEIGHT,
  PAGE_MARGIN,
} from 'constants/contentStyling/options';

const useStyles = makeStyles((theme) => ({
  appearanceContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1),
    maxHeight: 'calc(100vh - 170px)',
    overflowY: 'scroll',
    [theme.breakpoints.down('xs')]: {
      maxHeight: 'calc(100vh - 60px)', // vh minus title bar height
    },
  },
  controlsContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: theme.spacing(1),
    gap: theme.spacing(2),
  },
  resetAppearanceButton: {
    height: theme.spacing(5.5),
    '&:hover': {
      backgroundColor: theme.palette.shelfBlue['50L'],
    },
  },
  zoomFontSizeSelectGroup: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  zoomGroup: {
    marginTop: theme.spacing(-0.5),
  },
  fieldset: {
    border: 0,
    padding: 0,
    margin: 0,
    minWidth: 0,
  },
  legend: {
    marginLeft: theme.spacing(1),
    color: '#757575',
    fontWeight: 400,
    lineHeight: 1,
  },
  zoomControlButton: {
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    border: '1px solid lightgrey',
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: theme.palette.shelfBlue['50L'],
    },
    '& .MuiTouchRipple-root .MuiTouchRipple-child': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiNativeSelect-select': {
    padding: theme.spacing(1.5),
    margin: 0,
    minWidth: theme.spacing(2),
    textOverflow: 'ellipsis',
    height: theme.spacing(2.5),
  },
}));

const getFontSizePercentage = (value) => {
  return Math.round(value * 1000) / 10;
};

const getAriaLiveRegionMessage = (value, SETTING_CONSTANT, announceMessage) => {
  const settingsOption = SETTING_CONSTANT.find((option) => {
    return option['value'] === value;
  });

  return (
    <>
      {announceMessage}{' '}
      <span className="noTranslate">{settingsOption.label}.</span>
    </>
  );
};

const getDirection = (newFontSize, previousFontSize) => {
  return newFontSize > previousFontSize ? 'increased' : 'decreased';
};

const AppearanceSettingsMenu = ({ isFixedWidth = false, ...props }) => {
  const {
    setFontFamily,
    setFontSize,
    setTheme,
    setLetterSpacing,
    setLineHeight,
    setPageMargin,
    setLastFontSizeWasFit,
    setPreviousFontSize,
    resetBookStyling,
    handleZoomFit,
  } = useContext(BookAppearanceModifiersContext);

  const {
    fontFamily,
    fontSize,
    theme,
    letterSpacing,
    lineHeight,
    pageMargin,
    previousFontSize,
    lastFontSizeWasFit,
  } = useContext(BookAppearanceValuesContext);

  const classes = useStyles();

  const menuFontSize = fontSize < 0 ? 1 : fontSize;

  const font_sizes_with_current = FONT_SIZE.map((obj) => obj.value);
  if (!font_sizes_with_current.includes(menuFontSize)) {
    font_sizes_with_current.push(menuFontSize);
    font_sizes_with_current.sort();
  }

  const ariaLiveMessage = useRef();

  const setNewFontSize = (direction) => {
    // find the first size above the current size
    // use `font_sizes_with_current` to include fixedWidth fit to screen size
    let i = font_sizes_with_current.findIndex((s) => s === menuFontSize);
    direction === 'increased' ? (i += 1) : (i -= 1);

    // set new font size and aria live message
    setFontSize(font_sizes_with_current[i]);
    ariaLiveMessage.current = (
      <>
        Font size {direction} to{' '}
        <span className="noTranslate">
          {getFontSizePercentage(font_sizes_with_current[i])}%
        </span>
      </>
    );
  };

  const handleFontChange = (value) => {
    setFontFamily(value);
    ariaLiveMessage.current = getAriaLiveRegionMessage(
      value,
      FONT_FAMILY,
      'Font changed to',
    );
  };

  const handleFontSizeChange = (event) => {
    setLastFontSizeWasFit(false);
    setPreviousFontSize(menuFontSize);

    const value = !isNaN(event.target.value)
      ? +event.target.value
      : event.target.value;

    // set new font size and aria live message
    setFontSize(value);
    ariaLiveMessage.current = (
      <>
        Font size {getDirection(value, menuFontSize)} to{' '}
        <span className="noTranslate">{getFontSizePercentage(value)}%</span>
      </>
    );
  };

  const handleThemeChange = (value) => {
    setTheme(value);
    ariaLiveMessage.current = getAriaLiveRegionMessage(
      value,
      THEME,
      'Theme changed to',
    );
  };

  const handleLetterSpacingChange = (value) => {
    setLetterSpacing(value);
    ariaLiveMessage.current = getAriaLiveRegionMessage(
      value,
      LETTER_SPACING,
      'Letter spacing changed to',
    );
  };

  const handleLineHeightChange = (value) => {
    setLineHeight(value);
    ariaLiveMessage.current = getAriaLiveRegionMessage(
      value,
      LINE_HEIGHT,
      'Line height changed to',
    );
  };

  const handlePageMarginChange = (value) => {
    setPageMargin(value);
    ariaLiveMessage.current = getAriaLiveRegionMessage(
      value,
      PAGE_MARGIN,
      'Page margin changed to',
    );
  };

  const handleResetToDefault = () => {
    resetBookStyling();
    ariaLiveMessage.current = `All appearance settings reset to default.`;
  };

  const handleZoomOut = () => {
    setLastFontSizeWasFit(false);
    setPreviousFontSize(menuFontSize);
    setNewFontSize('decreased');
  };

  const handleZoomIn = () => {
    setLastFontSizeWasFit(false);
    setPreviousFontSize(menuFontSize);
    setNewFontSize('increased');
  };

  useEffect(
    function updateAriaLiveMessageForZoomFit() {
      if (Boolean(lastFontSizeWasFit)) {
        ariaLiveMessage.current = (
          <>
            Font size {getDirection(menuFontSize, previousFontSize)} to{' '}
            <span className="noTranslate">
              {getFontSizePercentage(menuFontSize)}%
            </span>
          </>
        );
      }
    },
    [menuFontSize, lastFontSizeWasFit, previousFontSize],
  );

  return (
    <div className={classes.appearanceContainer} {...props}>
      <AriaLiveRegion
        id="appearance-settings"
        message={ariaLiveMessage.current}
      />
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>
          {isFixedWidth ? 'Zoom' : 'Font Size'}
        </legend>
        <div className={clsx(classes.controlsContainer, classes.zoomGroup)}>
          <IconTooltip
            disabled={menuFontSize >= MAX_FONT_SIZE_VALUE}
            id="zoom-in"
            title="Zoom In"
          >
            <IconButton
              aria-label="Zoom In"
              className={classes.zoomControlButton}
              disabled={menuFontSize >= MAX_FONT_SIZE_VALUE}
              onClick={handleZoomIn}
            >
              <ZoomIn />
            </IconButton>
          </IconTooltip>
          <IconTooltip id="zoom-out" title="Zoom Out">
            <IconButton
              aria-label="Zoom Out"
              className={classes.zoomControlButton}
              disabled={menuFontSize <= MIN_FONT_SIZE_VALUE}
              onClick={handleZoomOut}
            >
              <ZoomOut />
            </IconButton>
          </IconTooltip>
          <div className={classes.zoomFontSizeSelectGroup}>
            <FormControl fullWidth variant="outlined">
              <StyledSelect
                inputProps={{
                  'aria-label': `Select a ${
                    isFixedWidth ? 'zoom level' : 'font size'
                  }`,
                  id: 'zoom-font-size-select',
                }}
                native
                onChange={handleFontSizeChange}
                value={menuFontSize}
                variant="outlined"
              >
                {font_sizes_with_current.map((value) => (
                  <option key={`zoom-font-size-${value}`} value={value}>
                    {`${getFontSizePercentage(value)}%`}
                  </option>
                ))}
              </StyledSelect>
            </FormControl>
          </div>
          {isFixedWidth && (
            <IconTooltip id="fit-to-screen" title="Fit to Screen">
              <IconButton
                aria-label="Fit To Screen"
                className={classes.zoomControlButton}
                onClick={handleZoomFit}
              >
                <ZoomOutMap />
              </IconButton>
            </IconTooltip>
          )}
        </div>
      </fieldset>
      <div className={classes.controlsContainer}>
        <SelectMenu
          label="theme"
          onChange={handleThemeChange}
          SELECT_OPTIONS={
            isFixedWidth
              ? [THEME[THEME_DEFAULT], THEME[THEME_HIGH_CONTRAST]]
              : THEME
          }
          value={theme}
        />
      </div>
      {!isFixedWidth ? (
        <>
          <div className={classes.controlsContainer}>
            <SelectMenu
              label="font"
              onChange={handleFontChange}
              SELECT_OPTIONS={FONT_FAMILY}
              value={fontFamily}
            />

            <SelectMenu
              label="letter-spacing"
              onChange={handleLetterSpacingChange}
              SELECT_OPTIONS={LETTER_SPACING}
              value={letterSpacing}
            />
          </div>
          <div className={classes.controlsContainer}>
            <SelectMenu
              label="line-height"
              onChange={handleLineHeightChange}
              SELECT_OPTIONS={LINE_HEIGHT}
              value={lineHeight}
            />

            <SelectMenu
              label="margins"
              onChange={handlePageMarginChange}
              SELECT_OPTIONS={PAGE_MARGIN}
              value={pageMargin}
            />
          </div>
        </>
      ) : null}
      <div className={classes.controlsContainer}>
        <Button
          id="reset-to-default-button"
          className={classes.resetAppearanceButton}
          disableFocusRipple
          startIcon={<History />}
          onClick={handleResetToDefault}
          color="primary"
          variant="outlined"
          fullWidth
        >
          Reset to Default
        </Button>
      </div>
    </div>
  );
};

AppearanceSettingsMenu.propTypes = {
  isFixedWidth: PropTypes.bool.isRequired,
};

export default AppearanceSettingsMenu;
